import { useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { BREAKPOINTS } from '@voll-intelligence/ui-kit/breakpoints';

/**
 * The two drawings of an offer row: `wide` is the desktop `.fq-row` (legs side
 * by side, price rail on the right); `stack` is the phone card (#3874 — legs
 * stacked, price strip below, badges bleeding over the top edge).
 */
export type OfferLayout = 'wide' | 'stack';

/**
 * Which drawing the list's CONTAINER asks for. It measures the `<ul>` itself
 * (not the viewport): the same list lives in the chat column, in the filter
 * drawer and in the explorer, and the `sm` ruler applies to the width each one
 * gives it — the same rule the sheet's `@container (width < sm)` block uses.
 *
 * A JS measurement and not a container query because the two drawings differ
 * in TREE, not only in style: the phone card moves the condition/duration from
 * the leg head to the connection line, turns the price rail into a strip, and
 * drops the pin from the actions row into the badge rail. Rendering both trees
 * and hiding one would double every text node for screen readers.
 *
 * Starts `wide` and only flips after a real measurement: jsdom (no layout,
 * `clientWidth` 0) and hosts without `ResizeObserver` keep the desktop row.
 */
export function useOfferLayout<T extends HTMLElement>(): {
  ref: RefObject<T | null>;
  layout: OfferLayout;
} {
  const ref = useRef<T>(null);
  const [layout, setLayout] = useState<OfferLayout>('wide');
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const measure = (width: number) => {
      setLayout(width > 0 && width < BREAKPOINTS.sm ? 'stack' : 'wide');
    };
    measure(el.clientWidth);
    const ro = new ResizeObserver((entries) => {
      measure(entries[0]?.contentRect.width ?? el.clientWidth);
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
    };
  }, []);
  return { ref, layout };
}
